import { Dispatch } from 'redux'

import { tryGetAllAccounts } from 'redux/actions/Accounts'
import { tryGetAllUserRoles } from 'redux/actions/AccountRoles'
import { tryGetAllAttributeOptions } from 'redux/actions/AttributeOptions'
import { tryGetAllCompanyGroups } from 'redux/actions/CompanyGroups'
import { tryGetAllDashboardGroups } from 'redux/actions/DashboardGroups'
import { tryGetAllDashboardFolders } from 'redux/actions/DashboardFolders'
import { tryGetAllGroups } from 'redux/actions/Groups'
import { tryGetAllMetrics, tryGetHeadlineMetric } from 'redux/actions/Metrics'
import { tryGetAllEntities } from 'redux/actions/Entities'
import { tryGetAllSlices } from 'redux/actions/Slices'
import { tryGetAllCategories } from 'redux/actions/Categories'
import { tryGetAllCustomFields } from 'redux/actions/CustomFields'
import { tryGetAllFilterOptions } from 'redux/actions/FilterOptions'
import { tryGetAllTagOptions } from 'redux/actions/TagOptions'
import { tryGetAllKeyMetrics } from 'redux/actions/KeyMetrics'
import { tryGetAllReports } from 'redux/actions/Reports'
import { tryGetAllReportEditions } from 'redux/actions/ReportEditions'
import { tryGetResourceStatuses } from 'redux/actions/ResourceStatuses'
import { tryGetMyScopes } from 'redux/actions/MyScopes'
import { tryGetAvailableUnits } from 'redux/actions/AvailableUnits'
import { tryGetOrganizationalUnits } from 'redux/actions/OrganizationalUnits'
import { tryGetLatestJobRun } from 'redux/actions/DataSettings'
import { tryGetAllAnalyses } from 'redux/actions/Analyses'
import { initializeGlobalFilterFromState } from 'redux/actions/GlobalFilter'
import { initializeCurrencyConverter } from 'redux/actions/CurrencyConverter'
import { initializeTestEnvironment } from 'redux/actions/TestEnvironment'

import { EAccountRole } from 'types/GlobalAccount'
import { ApplicationState } from 'redux/Stores/types'
import { selectAllowAccessAnalyses } from 'redux/selectors/Permissions'
import { selectCustomer } from 'redux/selectors/Customer'
import { selectSignedInAccount } from 'redux/selectors/SignedInAccount'

import * as appInitActions from './actions'
import { useThunkActions } from 'redux/actions/useThunkActions'

/**
 * The single owner of the app initialization sequence. Called from
 * App/Auth after tryValidateUser (redux/actions/Authentication) has
 * populated the signed-in state:
 *
 *   Phase 1: await the critical fetches the global filter derivation
 *            needs (available units, company groups) — skipped for
 *            company-group users, who have neither
 *   Phase 2: derive state (global filter from localStorage, currency
 *            converter) and mark the app ready
 *
 * The remaining fetches (startBackgroundFetches) are fire-and-forget and
 * kicked off immediately — nothing should wait for them; each consumer
 * reacts to its own store as the data arrives.
 *
 * Pages that need the signed-in context and global filter before
 * fetching gate on useAppReady() (redux/hooks/AppInit).
 */
export function initializeApp() {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    // Guard against double invocation (e.g. React StrictMode re-running
    // the calling effect).
    if (getState().AppInitStore.status !== 'idle') {
      return
    }

    const account = selectSignedInAccount(getState())

    if (!account) {
      return
    }

    dispatch(appInitActions.initStarted())

    // Before any fetch: the axios interceptor reads the active test
    // environment variant from localStorage on every request.
    initializeTestEnvironment(account.id)(dispatch)

    startBackgroundFetches(dispatch, getState)

    const criticalFetches = account.is_company_group
      ? []
      : [tryGetAvailableUnits()(dispatch), tryGetAllCompanyGroups(dispatch)]

    // Neither fetch ever rejects (failures dispatch *_FAILED), so
    // initialization always completes — the global filter falls
    // back to ALL if data is missing.
    Promise.all(criticalFetches)
      .then(() => {
        initializeGlobalFilterFromState()(dispatch, getState)
        initializeCurrencyConverter()(dispatch, getState)

        dispatch(appInitActions.initReady())
      })
      .catch(() => {
        dispatch(appInitActions.initFailed({ error: 'init_failed' }))
      })
  }
}

/**
 * All non-critical startup fetches. Fire-and-forget: nothing awaits
 * these, consumers react to their stores as data arrives.
 */
function startBackgroundFetches(
  dispatch: Dispatch,
  getState: () => ApplicationState
) {
  const account = selectSignedInAccount(getState())
  const customer = selectCustomer(getState())

  if (!account) {
    return
  }

  tryGetLatestJobRun(dispatch)
  tryGetAllCustomFields(dispatch)

  // Fetch misc for logged in users
  tryGetAllGroups(dispatch)
  tryGetAllDashboardGroups(dispatch)
  tryGetAllDashboardFolders(dispatch)
  tryGetResourceStatuses(dispatch)

  // categories
  tryGetAllCategories(dispatch)

  // kpis
  tryGetAllMetrics()(dispatch)

  // attribute-options: needed by the metric charts in the "Utforska"
  // dialog, which company-group contexts can reach too, so fetch for all users
  tryGetAllAttributeOptions()(dispatch)

  // entities
  tryGetAllEntities()(dispatch)

  // slices
  tryGetAllSlices()(dispatch)

  if (account.role === EAccountRole.ADMIN || account.is_homepal_user) {
    tryGetOrganizationalUnits()(dispatch)
  }

  if (selectAllowAccessAnalyses(getState())) {
    tryGetAllAnalyses()(dispatch, getState)
    tryGetHeadlineMetric()(dispatch)
  }

  if (!account.is_company_group) {
    // Fetch misc for logged in users and is not is_company_group

    if (customer?.allow_access.allow_key_metrics) {
      tryGetAllKeyMetrics(dispatch)
    }

    if (customer?.allow_access.allow_reports) {
      tryGetAllReports()(dispatch)
      tryGetAllReportEditions()(dispatch)
    }

    // accounts
    tryGetAllAccounts(dispatch)
    tryGetAllUserRoles(dispatch)

    // my-scopes (real estates or companies depending on access type)
    tryGetMyScopes()(dispatch)

    // filter-options
    tryGetAllFilterOptions(dispatch)

    // tag-options
    tryGetAllTagOptions(dispatch)
  }
}

export const useAppInitActions = () => {
  return {
    initializeApp: useThunkActions(initializeApp)
  }
}
